import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Secure token storage abstraction.
 *
 * On native (iOS/Android) uses expo-secure-store which encrypts values
 * via the platform keychain / EncryptedSharedPreferences.
 * On web, falls back to AsyncStorage (localStorage) since SecureStore
 * is not available in the browser.
 */

let SecureStore: typeof import('expo-secure-store') | null = null;

if (Platform.OS !== 'web') {
  try {
    // Dynamic require avoids bundling SecureStore on web
    SecureStore = require('expo-secure-store');
  } catch {
    // Fallback if SecureStore not available (e.g. Expo Go on older SDK)
  }
}

/**
 * Sanitize key for expo-secure-store which only allows [a-zA-Z0-9._-].
 * AsyncStorage accepts any string key, so only SecureStore paths need this.
 */
function sanitizeKeyForSecureStore(key: string): string {
  return key.replace(/[^a-zA-Z0-9._-]/g, '_');
}

export async function setSecureItem(key: string, value: string): Promise<void> {
  if (SecureStore) {
    await SecureStore.setItemAsync(sanitizeKeyForSecureStore(key), value);
  } else {
    await AsyncStorage.setItem(key, value);
  }
}

export async function getSecureItem(key: string): Promise<string | null> {
  if (SecureStore) {
    return SecureStore.getItemAsync(sanitizeKeyForSecureStore(key));
  }
  return AsyncStorage.getItem(key);
}

export async function deleteSecureItem(key: string): Promise<void> {
  if (SecureStore) {
    await SecureStore.deleteItemAsync(sanitizeKeyForSecureStore(key));
  } else {
    await AsyncStorage.removeItem(key);
  }
}
